import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  LayoutGrid, 
  RefreshCw, 
  Printer, 
  ArrowRight,
  CheckCircle2,
  Clock,
  FileText,
  AlertTriangle,
  AlertCircle,
  ScrollText,
  HeartHandshake,
  Check,
  Loader2,
  Users,
  ChevronRight,
  Sparkles,
  Lock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useLifely } from '../context/LifelyContext';
import UpgradeModal from '../components/billing/UpgradeModal';
import VaultHealthCard from '../components/dashboard/VaultHealthCard';
import VaultGapsCard from '../components/dashboard/VaultGapsCard';
import { vaultHealthAPI } from '../services/api';
import { toast } from 'sonner';

// Progress Stepper Component
const ProgressStepper = ({ stats, contactCount }) => {
  // Check if there's anything to export
  const hasDataToExport = contactCount > 0 || 
                          stats.willExists === 'yes' || 
                          stats.totalEntries > 0;

  const steps = [
    {
      id: 'contacts',
      label: 'Contacts',
      path: '/contacts',
      description: 'Add your people',
      isComplete: contactCount > 0,
      count: contactCount,
      countLabel: 'contacts'
    },
    {
      id: 'formal',
      label: 'Formal',
      path: '/will-status',
      description: 'Legal arrangements',
      isComplete: stats.willExists === 'yes',
      status: stats.willExists === 'yes' ? 'Complete' : stats.willExists === 'no' ? 'No will' : 'Not set'
    },
    {
      id: 'maps',
      label: 'Maps',
      path: '/maps',
      description: 'Document assets',
      isComplete: stats.totalEntries > 0,
      count: stats.totalEntries,
      countLabel: 'entries'
    },
    {
      id: 'continuity',
      label: 'Continuity',
      path: '/continuity',
      description: 'Set up check-ins',
      isComplete: stats.continuityConfigured,
      status: stats.continuityConfigured ? 'Active' : 'Not set'
    },
    {
      id: 'export',
      label: 'Export',
      path: '/print-export',
      description: 'Share manually',
      isComplete: hasDataToExport,
      status: hasDataToExport ? 'Ready' : 'Add data first'
    }
  ];

  const completedCount = steps.filter(s => s.isComplete).length;

  return (
    <div className="space-y-4" data-testid="progress-stepper">
      {/* Hint text */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span className="font-medium text-foreground">Getting started?</span>
        <span>Work through each step from left to right to get the most out of Lifely.</span>
      </div>
      
      {/* Stepper */}
      <div className="relative">
        {/* Progress line background */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-border hidden sm:block" />
        
        {/* Steps */}
        <div className="grid grid-cols-5 gap-2 sm:gap-0">
          {steps.map((step, index) => (
            <Link
              key={step.id}
              to={step.path}
              className="group relative flex flex-col items-center text-center"
              data-testid={`step-${step.id}`}
            >
              {/* Step number/check circle */}
              <div className={`
                relative z-10 w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium
                transition-all duration-200 group-hover:scale-110
                ${step.isComplete 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted border-2 border-border text-muted-foreground group-hover:border-primary/50'
                }
              `}>
                {step.isComplete ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>
              
              {/* Step label */}
              <div className="mt-2 space-y-0.5">
                <p className={`text-xs font-medium ${step.isComplete ? 'text-foreground' : 'text-muted-foreground'} group-hover:text-foreground`}>
                  {step.label}
                </p>
                <p className="text-[10px] text-muted-foreground hidden sm:block">
                  {step.count !== undefined 
                    ? `${step.count} ${step.countLabel}`
                    : step.status
                  }
                </p>
              </div>
              
              {/* Connector arrow (hidden on last item and mobile) */}
              {index < steps.length - 1 && (
                <ChevronRight className="absolute top-3 -right-1 w-4 h-4 text-border hidden sm:block" />
              )}
            </Link>
          ))}
        </div>
      </div>
      
      {/* Progress summary */}
      <p className="text-xs text-muted-foreground text-center sm:text-left">
        {completedCount === 0 
          ? "You haven't started yet - begin with Contacts to add your people."
          : completedCount === steps.length
            ? "Almost there! Your Lifely is ready to export whenever you need."
            : `${completedCount} of ${steps.length} steps complete`
        }
      </p>
    </div>
  );
};

const Dashboard = () => {
  const {
    user,
    isAuthenticated,
    authLoading,
    tokenProviderReady,
    getSummaryStats,
    continuityConfig,
    performCheckIn,
    fetchMaps,
    fetchContacts,
    fetchWillStatus,
    fetchContinuity,
    fetchSharing,
    loading,
    contacts,
  } = useLifely();
  
  const [justCheckedIn, setJustCheckedIn] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [vaultHealth, setVaultHealth] = useState(null);
  const [vaultHealthLoading, setVaultHealthLoading] = useState(true);
  const [vaultHealthError, setVaultHealthError] = useState(false);

  // AI Assistant is Standard-only; trial users see it locked.
  const canUseAI = ['standard', 'plus'].includes(user?.plan);
  const isTrial = user?.plan === 'trial';

  // Fetch data when authenticated and token provider is ready
  useEffect(() => {
    const loadData = async () => {
      if (isAuthenticated && tokenProviderReady && !dataLoaded) {
        try {
          await Promise.all([
            fetchMaps(),
            fetchContacts(),
            fetchWillStatus(),
            fetchContinuity(),
            fetchSharing(),
          ]);
          setDataLoaded(true);
        } catch (err) {
          console.error('Failed to load dashboard data:', err);
        }
      }
    };
    loadData();
  }, [isAuthenticated, tokenProviderReady, dataLoaded, fetchMaps, fetchContacts, fetchWillStatus, fetchContinuity, fetchSharing]);

  // Vault health (dashboard-specific, fetched directly — not in context).
  useEffect(() => {
    let cancelled = false;
    const loadVaultHealth = async () => {
      if (!isAuthenticated || !tokenProviderReady) return;
      setVaultHealthLoading(true);
      setVaultHealthError(false);
      try {
        const data = await vaultHealthAPI.getHealth();
        if (!cancelled) setVaultHealth(data);
      } catch (err) {
        console.error('Failed to load vault health:', err);
        if (!cancelled) setVaultHealthError(true);
      } finally {
        if (!cancelled) setVaultHealthLoading(false);
      }
    };
    loadVaultHealth();
    return () => { cancelled = true; };
  }, [isAuthenticated, tokenProviderReady]);

  const stats = getSummaryStats();

  const handleCheckIn = async () => {
    try {
      const result = await performCheckIn();
      setJustCheckedIn(true);
      const interval = continuityConfig?.checkInInterval || 2;
      const unit = continuityConfig?.checkInUnit || 'weeks';
      if (result?.nextCheckIn) {
        toast.success(`Checked in! Next check-in extended to ${new Date(result.nextCheckIn).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`);
      } else {
        toast.success(`Checked in! Next check-in in ${interval} ${unit}.`);
      }
      setTimeout(() => setJustCheckedIn(false), 3000);
    } catch (err) {
      toast.error('Check-in failed. Please try again.');
    }
  };

  // Helper to get holder label
  const getHolderLabel = (value) => {
    const labels = {
      'lawyer': 'Lawyer / Law Firm',
      'trustee': 'Trustee',
      'personal': 'Stored Personally',
      'other': 'Other'
    };
    return labels[value] || '-';
  };

  // Show loading state while fetching initial data
  const isLoading = loading.maps || loading.contacts || loading.willStatus || loading.continuity;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Welcome Section */}
      <section className="space-y-2">
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-foreground flex items-center gap-3 flex-wrap">
          Welcome{authLoading ? (
            <span className="inline-block w-32 h-7 bg-muted rounded-md animate-pulse" />
          ) : user?.firstName ? (
            <>, {user.firstName}</>
          ) : (
            <> back</>
          )}
        </h1>
        <p className="text-muted-foreground text-base max-w-2xl">
          Your calm space for mapping what matters. Take your time - there&apos;s no rush.
        </p>
      </section>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading your data...</span>
        </div>
      )}

      {/* Progress Stepper */}
      {!isLoading && (
        <section className="bg-muted/30 rounded-xl p-6 border border-border" data-testid="progress-section">
          <ProgressStepper stats={stats} contactCount={contacts?.length || 0} />
        </section>
      )}

      {/* Vault Health */}
      {!isLoading && (
        <VaultHealthCard data={vaultHealth} loading={vaultHealthLoading} error={vaultHealthError} />
      )}

      {/* What's missing (reads gaps[] from the same vault-health payload) */}
      {!isLoading && (
        <VaultGapsCard data={vaultHealth} loading={vaultHealthLoading} error={vaultHealthError} />
      )}

      {/* Summary Cards */}
      {!isLoading && (
        <>
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="summary-cards">
            {/* Maps Status Card */}
            <Card className="card-hover border border-border" data-testid="maps-status-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-medium font-body">
                  <LayoutGrid className="w-4 h-4 text-primary" />
                  Maps Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <span className="w-2 h-2 rounded-full bg-primary"></span>
                      Configured
                    </span>
                    <span className="font-medium">{stats.configuredMaps}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <span className="w-2 h-2 rounded-full bg-muted-foreground/40"></span>
                      Not Configured
                    </span>
                    <span className="font-medium">{stats.notConfiguredMaps}</span>
                  </div>
                </div>
                <div className="pt-2 border-t border-border space-y-1">
                  <p className="text-xs text-muted-foreground">
                    {stats.totalEntries} entries across {stats.totalMaps} maps
                  </p>
                  {(stats.criticalCount > 0 || stats.highCount > 0) && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {stats.criticalCount > 0 && (
                        <Badge variant="outline" className="text-xs bg-destructive/10 text-destructive border-destructive/20">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {stats.criticalCount} Critical
                        </Badge>
                      )}
                      {stats.highCount > 0 && (
                        <Badge variant="outline" className="text-xs bg-accent/20 text-accent border-accent/30">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          {stats.highCount} High
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Will Status Card */}
            <Card className="card-hover border border-border" data-testid="will-status-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-medium font-body">
                  <ScrollText className="w-4 h-4 text-primary" />
                  Formal Arrangements
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {stats.willExists === 'yes' ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium">Will exists</span>
                      </>
                    ) : stats.willExists === 'no' ? (
                      <>
                        <AlertTriangle className="w-4 h-4 text-destructive" />
                        <span className="text-sm text-destructive">No will</span>
                      </>
                    ) : (
                      <>
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Not specified</span>
                      </>
                    )}
                  </div>
                  {stats.willExists === 'yes' && stats.willHeldWith && (
                    <p className="text-sm text-muted-foreground">
                      Held with: {getHolderLabel(stats.willHeldWith)}
                    </p>
                  )}
                  {stats.willExists === 'yes' && stats.willFirmName && (
                    <p className="text-sm text-muted-foreground truncate">
                      {stats.willFirmName}
                    </p>
                  )}
                  {/* Reminder status */}
                  <div className="flex items-center gap-2 pt-1">
                    {stats.willReminderDate ? (
                      <>
                        <CheckCircle2 className="w-3 h-3 text-primary" />
                        <span className="text-xs text-muted-foreground">Review reminder configured</span>
                      </>
                    ) : (
                      <>
                        <Clock className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Review reminder not set</span>
                      </>
                    )}
                  </div>
                </div>
                <Link to="/will-status" className="block">
                  <Button variant="ghost" size="sm" className="w-full justify-start text-xs h-8 px-2">
                    <ArrowRight className="w-3 h-3 mr-1" />
                    View details
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Continuity Status Card */}
            <Card className="card-hover border border-border" data-testid="continuity-status-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-medium font-body">
                  <RefreshCw className="w-4 h-4 text-primary" />
                  Continuity Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {stats.continuityConfigured ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium">Active</span>
                      </>
                    ) : (
                      <>
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Not configured</span>
                      </>
                    )}
                  </div>
                  {stats.continuityConfigured && (
                    <>
                      <p className="text-sm text-muted-foreground">
                        {stats.nomineesCount} nominee{stats.nomineesCount !== 1 ? 's' : ''} added
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Check-in every {continuityConfig?.checkInInterval || continuityConfig?.checkInFrequency || 3} {continuityConfig?.checkInUnit || 'months'}
                      </p>
                    </>
                  )}
                </div>
                {!stats.continuityConfigured && (
                  <p className="text-xs text-muted-foreground">
                    Set up check-ins and nominees.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Activity Card */}
            <Card className="card-hover border border-border" data-testid="last-updated-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-medium font-body">
                  <FileText className="w-4 h-4 text-primary" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {stats.recentActivity.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recentActivity.slice(0, 3).map((activity, idx) => (
                      <div key={activity.mapId || activity.id || idx} className="text-xs space-y-0.5">
                        <div className="flex items-center justify-between">
                          <span className="text-foreground font-medium flex items-center gap-1">
                            {activity.type === 'map' && activity.mapName?.split(' ')[0]}
                            {activity.type === 'contact' && (
                              <>
                                <Users className="w-3 h-3" />
                                Contact
                              </>
                            )}
                            {activity.type === 'will' && (
                              <>
                                <ScrollText className="w-3 h-3" />
                                Formal
                              </>
                            )}
                          </span>
                          <span className="text-muted-foreground/60">
                            {activity.lastUpdated?.split('T')[0] || activity.lastUpdated}
                          </span>
                        </div>
                        <p className="text-muted-foreground truncate">
                          {activity.type === 'map' && activity.lastEntryLabel}
                          {activity.type === 'contact' && `${activity.name} (${activity.contactCount} total)`}
                          {activity.type === 'will' && `Will status: ${activity.willExists || 'Updated'}`}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No recent activity
                  </p>
                )}
                {stats.recentActivity.length > 3 && (
                  <p className="text-xs text-muted-foreground pt-1 border-t border-border">
                    +{stats.recentActivity.length - 3} more recent updates
                  </p>
                )}
              </CardContent>
            </Card>
          </section>

          {/* Check-in Section (only shown when continuity is configured) */}
          {stats.continuityConfigured && (
            <section data-testid="check-in-section">
              <Card className="border-2 border-primary/20 bg-primary/5" data-testid="check-in-card">
                <CardContent className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6">
                  <div className="flex items-center gap-4 text-center sm:text-left">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                      <HeartHandshake className="w-6 h-6 text-primary" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="font-medium text-foreground">Stay connected</h3>
                      {continuityConfig?.nextCheckIn ? (
                        <p className="text-sm text-muted-foreground">
                          Next check-in: <span className="font-medium text-foreground">{new Date(continuityConfig.nextCheckIn).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </p>
                      ) : continuityConfig?.lastCheckIn ? (
                        <p className="text-sm text-muted-foreground">
                          Last check-in: {new Date(continuityConfig.lastCheckIn).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No check-ins yet
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Extend your next check-in by {continuityConfig?.checkInInterval || continuityConfig?.checkInFrequency || 3} {continuityConfig?.checkInUnit || 'months'}. 
                        <Link to="/continuity/setup" className="text-primary hover:underline ml-1">Change in settings</Link>
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={handleCheckIn}
                    disabled={justCheckedIn}
                    className="shrink-0 min-w-[140px]"
                    data-testid="check-in-btn"
                  >
                    {justCheckedIn ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Checked In!
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Check In
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </section>
          )}

          {/* Primary Actions */}
          <section className="space-y-4" data-testid="primary-actions">
            <h2 className="text-lg font-heading font-medium text-foreground">
              Quick Actions
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Link to="/maps" data-testid="view-maps-action">
                <Card className="card-hover border border-border cursor-pointer group h-full">
                  <CardContent className="flex items-center justify-between p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <LayoutGrid className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">View Maps</p>
                        <p className="text-xs text-muted-foreground">Review and update your maps</p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-transform duration-200" />
                  </CardContent>
                </Card>
              </Link>

              <Link to="/continuity" data-testid="update-continuity-action">
                <Card className="card-hover border border-border cursor-pointer group h-full">
                  <CardContent className="flex items-center justify-between p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                        <RefreshCw className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Update Continuity</p>
                        <p className="text-xs text-muted-foreground">Configure check-ins and nominees</p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-accent group-hover:translate-x-1 transition-transform duration-200" />
                  </CardContent>
                </Card>
              </Link>

              <Link to="/print-export" data-testid="create-bundle-action">
                <Card className="card-hover border border-border cursor-pointer group h-full">
                  <CardContent className="flex items-center justify-between p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                        <Printer className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Create Printable Bundle</p>
                        <p className="text-xs text-muted-foreground">Export your maps for printing</p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground group-hover:translate-x-1 transition-transform duration-200" />
                  </CardContent>
                </Card>
              </Link>

              {/* AI Assistant — Standard-only; trial sees it locked */}
              {canUseAI && (
                <Link to="/ai-assist" data-testid="ai-assist-action">
                  <Card className="card-hover border border-border cursor-pointer group h-full">
                    <CardContent className="flex items-center justify-between p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Sparkles className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">AI Assistant</p>
                          <p className="text-xs text-muted-foreground">Describe an asset, we&apos;ll fill the form</p>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-transform duration-200" />
                    </CardContent>
                  </Card>
                </Link>
              )}
              {!canUseAI && isTrial && (
                <button
                  type="button"
                  onClick={() => setShowUpgrade(true)}
                  className="text-left w-full h-full"
                  data-testid="ai-assist-action-locked"
                >
                  <Card className="card-hover border border-border cursor-pointer group h-full">
                    <CardContent className="flex items-center justify-between p-6">
                      <div className="flex items-center gap-3">
                        <div className="relative w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Sparkles className="w-5 h-5 text-primary" />
                          <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-foreground text-background flex items-center justify-center">
                            <Lock className="w-2.5 h-2.5" />
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-sm">AI Assistant</p>
                          <p className="text-xs text-muted-foreground">Describe an asset, we&apos;ll fill the form</p>
                        </div>
                      </div>
                      <Lock className="w-4 h-4 text-muted-foreground" />
                    </CardContent>
                  </Card>
                </button>
              )}
            </div>
          </section>
        </>
      )}

      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} trigger="ai" />

    </div>
  );
};

export default Dashboard;
